import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchRestaurantCateringFarmhouses,
  mapRestaurantCateringFarmhouse,
  searchRestaurantCateringFarmhouses,
  unmapRestaurantCateringFarmhouse,
} from './restaurantApi';
import { formatSlotWindow, WEEKDAYS } from './DeliverySlotsEditor';
import './css/AgePricingEditor.css';

function hallIdOf(row) {
  return String(row?.partyHallId ?? row?.party_hall_id ?? '').trim();
}

function hallNameOf(row) {
  return String(row?.hallName ?? row?.hall_name ?? '').trim() || `Hall ${hallIdOf(row)}`;
}

function formatScheduleSummary(deliverySlotsPayload) {
  const mode = String(deliverySlotsPayload?.deliveryMode || 'REGULAR').toUpperCase();
  const duration = Number(deliverySlotsPayload?.slotDurationMinutes) || 30;
  const slots = deliverySlotsPayload?.deliverySlots || {};
  const nextLabel = String(deliverySlotsPayload?.nextDeliveryLabel || '').trim();

  if (mode === 'REGULAR') {
    return {
      modeLabel: 'Regular (ASAP)',
      nextLabel: nextLabel || 'Customers order for ASAP delivery.',
      dayLines: [],
    };
  }

  const dayLines = WEEKDAYS.map((day) => {
    const times = Array.isArray(slots?.[day]) ? slots[day] : [];
    if (!times.length) return null;
    const windows = times
      .map((t) => formatSlotWindow(t, duration))
      .filter(Boolean);
    if (!windows.length) return null;
    return `${day.slice(0, 3)}: ${windows.join(', ')}`;
  }).filter(Boolean);

  return {
    modeLabel: mode === 'BOTH' ? 'Fixed + Regular' : 'Fixed schedule',
    nextLabel: nextLabel || (dayLines.length ? null : 'No upcoming fixed slots configured.'),
    dayLines,
  };
}

/**
 * Map Farmhouses for catering — same idea as Map Offices for Work Eats.
 */
export default function CateringMapFarmhousesPanel({
  restaurantId,
  userId,
  disabled = false,
}) {
  const [mapped, setMapped] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [deliverySlots, setDeliverySlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [name, setName] = useState('');
  const [radiusKm, setRadiusKm] = useState('10');
  const [hallType, setHallType] = useState('FARMHOUSE');
  const [message, setMessage] = useState('');

  const mappedIds = useMemo(
    () => new Set(mapped.map((row) => hallIdOf(row)).filter(Boolean)),
    [mapped],
  );

  const scheduleSummary = useMemo(
    () => formatScheduleSummary(deliverySlots),
    [deliverySlots],
  );

  const loadMapped = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setMessage('');
    try {
      const data = await fetchRestaurantCateringFarmhouses(restaurantId);
      setMapped(Array.isArray(data?.farmhouses) ? data.farmhouses : []);
      setDeliverySlots(data?.deliverySlots || null);
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to load mapped farmhouses.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  const searchNearby = useCallback(async () => {
    if (!restaurantId) return;
    setSearching(true);
    setMessage('');
    try {
      const farmhouses = await searchRestaurantCateringFarmhouses(restaurantId, {
        name: name.trim(),
        radiusKm: Number(radiusKm) || 10,
        hallType,
      });
      setNearby(farmhouses);
      if (!farmhouses.length) {
        setMessage(`No farmhouses found within ${radiusKm || 10} km.`);
      }
    } catch (error) {
      setNearby([]);
      setMessage(error?.response?.data?.error || error?.message || 'Failed to search farmhouses.');
    } finally {
      setSearching(false);
    }
  }, [hallType, name, radiusKm, restaurantId]);

  useEffect(() => {
    void loadMapped();
  }, [loadMapped]);

  useEffect(() => {
    if (!restaurantId) return undefined;
    const t = setTimeout(() => {
      void searchNearby();
    }, 350);
    return () => clearTimeout(t);
  }, [restaurantId, name, radiusKm, hallType, searchNearby]);

  const handleMap = async (partyHallId) => {
    if (!restaurantId || !userId || !partyHallId) return;
    setActionId(String(partyHallId));
    setMessage('');
    try {
      const data = await mapRestaurantCateringFarmhouse(restaurantId, partyHallId, userId);
      const farmhouses = Array.isArray(data?.farmhouses)
        ? data.farmhouses
        : (await fetchRestaurantCateringFarmhouses(restaurantId))?.farmhouses || [];
      setMapped(farmhouses);
      setNearby((prev) =>
        prev.map((row) =>
          hallIdOf(row) === String(partyHallId) ? { ...row, mapped: true } : row,
        ),
      );
      setMessage('Farmhouse mapped for catering.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to map farmhouse.');
    } finally {
      setActionId(null);
    }
  };

  const handleUnmap = async (partyHallId) => {
    if (!restaurantId || !partyHallId) return;
    setActionId(String(partyHallId));
    setMessage('');
    try {
      const data = await unmapRestaurantCateringFarmhouse(restaurantId, partyHallId);
      const farmhouses = Array.isArray(data?.farmhouses)
        ? data.farmhouses
        : (await fetchRestaurantCateringFarmhouses(restaurantId))?.farmhouses || [];
      setMapped(farmhouses);
      setNearby((prev) =>
        prev.map((row) =>
          hallIdOf(row) === String(partyHallId) ? { ...row, mapped: false } : row,
        ),
      );
      setMessage('Farmhouse unmapped.');
    } catch (error) {
      setMessage(error?.response?.data?.error || error?.message || 'Failed to unmap farmhouse.');
    } finally {
      setActionId(null);
    }
  };

  const combinedRows = useMemo(() => {
    const byId = new Map();
    for (const row of mapped) {
      const id = hallIdOf(row);
      if (!id) continue;
      byId.set(id, { ...row, mapped: true });
    }
    for (const row of nearby) {
      const id = hallIdOf(row);
      if (!id) continue;
      const existing = byId.get(id);
      byId.set(id, {
        ...(existing || {}),
        ...row,
        mapped: existing ? true : Boolean(row.mapped || mappedIds.has(id)),
      });
    }
    return [...byId.values()].sort((a, b) => {
      const da = a.distanceKm == null ? Number.POSITIVE_INFINITY : Number(a.distanceKm);
      const db = b.distanceKm == null ? Number.POSITIVE_INFINITY : Number(b.distanceKm);
      if (da !== db) return da - db;
      return hallNameOf(a).localeCompare(hallNameOf(b));
    });
  }, [mapped, mappedIds, nearby]);

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">Map Farmhouses (Catering)</label>
        <p className="age-pricing-editor__hint">
          Link farmhouses / party halls this caterer can deliver to. Customers pick a mapped
          farmhouse in MyEats catering, then select menu items and request a quote.
        </p>
      </div>

      <div className="age-pricing-editor__header" style={{ marginTop: 8 }}>
        <label className="age-pricing-editor__title" style={{ fontSize: 14 }}>
          Delivery schedule
        </label>
        <p className="age-pricing-editor__hint">
          Mode: <strong>{scheduleSummary.modeLabel}</strong>
          {scheduleSummary.nextLabel ? ` — ${scheduleSummary.nextLabel}` : ''}
        </p>
        {scheduleSummary.dayLines.length ? (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#475569', fontSize: 13 }}>
            {scheduleSummary.dayLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
        <p className="age-pricing-editor__hint" style={{ marginTop: 6 }}>
          Configure windows under the Delivery Options tab (MYEATS delivery schedule).
        </p>
      </div>

      <div className="get-restaurant-form-row flex" style={{ gap: 8, marginBottom: 10 }}>
        <div className="get-restaurant-form-group">
          <label>Search hall name</label>
          <input
            type="text"
            value={name}
            disabled={disabled || searching}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Green Valley Farmhouse"
          />
        </div>
        <div className="get-restaurant-form-group">
          <label>Type</label>
          <select
            value={hallType}
            disabled={disabled || searching}
            onChange={(e) => setHallType(e.target.value)}
          >
            <option value="FARMHOUSE">Farmhouse</option>
            <option value="BANQUET">Banquet</option>
            <option value="WEDDING">Wedding</option>
            <option value="ALL">All hall types</option>
          </select>
        </div>
        <div className="get-restaurant-form-group">
          <label>Radius (km)</label>
          <input
            type="number"
            min="1"
            max="50"
            value={radiusKm}
            disabled={disabled || searching}
            onChange={(e) => setRadiusKm(e.target.value)}
          />
        </div>
        <div className="get-restaurant-form-group" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || searching || !restaurantId}
            onClick={() => void searchNearby()}
          >
            {searching ? 'Searching…' : 'Search nearby'}
          </button>
        </div>
      </div>

      {message ? <p className="age-pricing-editor__hint">{message}</p> : null}
      {loading ? <p className="age-pricing-editor__hint">Loading mapped farmhouses…</p> : null}

      <div className="table-responsive">
        <table className="menu-table">
          <thead>
            <tr>
              <th>Farmhouse</th>
              <th>Type</th>
              <th>Venue</th>
              <th>Distance</th>
              <th>Address</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {combinedRows.length === 0 ? (
              <tr>
                <td colSpan={6}>No farmhouses to show yet. Search nearby to map venues.</td>
              </tr>
            ) : (
              combinedRows.map((row) => {
                const id = hallIdOf(row);
                const busy = actionId === id;
                const isMapped = Boolean(row.mapped);
                return (
                  <tr key={id}>
                    <td>{hallNameOf(row)}</td>
                    <td>{row.hallTypeLabel || row.hallType || '—'}</td>
                    <td>{row.venueRestaurantName || '—'}</td>
                    <td>
                      {row.distanceKm != null && Number.isFinite(Number(row.distanceKm))
                        ? `${Number(row.distanceKm).toFixed(1)} km`
                        : '—'}
                    </td>
                    <td>{row.addressLine || '—'}</td>
                    <td>
                      {isMapped ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={disabled || busy}
                          onClick={() => void handleUnmap(id)}
                        >
                          {busy ? '…' : 'Remove'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary"
                          disabled={disabled || busy || !userId}
                          onClick={() => void handleMap(id)}
                        >
                          {busy ? '…' : 'Add'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
